import { groupBy, orderBy, uniq } from 'lodash';
import { DISPLAY_BY } from 'utils/constants';

import { ISpendByType, ISpendContribution, IDisplayByType } from 'utils/types';

const OTHERS = 'Others';

export const hasChildren = (
  category: string,
  spendItems: ISpendContribution[]
) => {
  return spendItems
    .filter((item) => item.dimValueL1 === category)
    .reduce((acc, item) => {
      return acc || Boolean(item.dimValueL2);
    }, false);
};

export const getCategories = ({
  displayBy,
  selectedSpendBy,
  spendItems,
}: {
  displayBy: IDisplayByType;
  selectedSpendBy?: string;
  spendItems: ISpendContribution[];
}) => {
  const orderProp =
    displayBy === DISPLAY_BY.HEADCOUNT ? 'numberOfEmployees' : 'cost';
  let categories = [];
  if (selectedSpendBy && hasChildren(selectedSpendBy, spendItems)) {
    const filteredItems = spendItems.filter(
      (it) => it.dimValueL1 === selectedSpendBy
    );
    const orderedItems = orderBy(filteredItems || [], orderProp, 'desc');
    categories = uniq(orderedItems.map((item) => item.dimValueL2));
  } else {
    const orderedItems = orderBy(spendItems, orderProp, 'desc');
    categories = uniq(orderedItems.map((item) => item.dimValueL1));
  }
  if (categories.length > 20) {
    const topCategories = categories.splice(0, 19);
    return {
      categories: [...topCategories, OTHERS],
      otherCategories: categories,
    };
  }
  return { categories, otherCategories: [] };
};

export const createSpendByChartData = ({
  categories,
  displayBy,
  otherCategories,
  isPersonnel,
  permanentContractorChecked,
  revenue,
  selectedSpendBy,
  spendBy,
  spendItems,
}: {
  categories: string[];
  displayBy: IDisplayByType;
  otherCategories: string[];
  isPersonnel: boolean;
  permanentContractorChecked: boolean;
  revenue: number;
  selectedSpendBy?: string;
  spendBy: ISpendByType;
  spendItems: ISpendContribution[];
}) => {
  if (spendItems.length === 0) {
    return { categories: [], series: [] };
  }
  const isFilterByPermanentContractor =
    isPersonnel && permanentContractorChecked;
  const groupedFilteredItems = groupBy(
    spendItems,
    isFilterByPermanentContractor ? 'permanentOrContract' : 'contribution'
  );
  const series = Object.keys(groupedFilteredItems).map((contribution) => {
    const groupedByContribution = groupedFilteredItems[contribution];
    return {
      name: contribution,
      data: categories.map((dimValueL1) => {
        if (dimValueL1 === OTHERS) {
          return otherCategories.reduce((acc, otherCategory) => {
            const item = groupedByContribution.find(
              (it) => it.dimValueL1 === otherCategory
            );
            return (
              acc +
              getItemDataValue({
                item,
                displayBy,
                revenue,
              })
            );
          }, 0);
        }
        return getCategoryData({
          category: dimValueL1,
          contribution,
          displayBy,
          isFilterByPermanentContractor,
          permanentContractorChecked,
          revenue,
          selectedSpendBy,
          spendBy,
          spendItems,
        });
      }),
    };
  });

  return {
    categories,
    series,
  };
};

export const getItemDataValue = ({
  item,
  displayBy,
  revenue,
}: {
  item?: ISpendContribution;
  displayBy: IDisplayByType;
  revenue: number;
}) => {
  if (displayBy === DISPLAY_BY.PERCENTAGE_OF_REVENUE) {
    return ((item ? item.cost : 0) / (revenue || 1)) * 100;
  }
  if (displayBy === DISPLAY_BY.HEADCOUNT) {
    return item && item.numberOfEmployees ? item.numberOfEmployees : 0;
  }
  return item ? item.cost : 0;
};

export const getCategoryData = ({
  category,
  contribution,
  displayBy,
  isFilterByPermanentContractor,
  revenue,
  selectedSpendBy,
  spendBy,
  spendItems,
}: {
  category: string;
  contribution: string;
  displayBy: IDisplayByType;
  isFilterByPermanentContractor: boolean;
  permanentContractorChecked: boolean;
  revenue: number;
  selectedSpendBy?: string;
  spendBy: ISpendByType;
  spendItems: ISpendContribution[];
}) => {
  const groupedFilteredItems = groupBy(
    spendItems,
    isFilterByPermanentContractor ? 'permanentOrContract' : 'contribution'
  );
  const groupedByContribution = groupedFilteredItems[contribution];
  let items = [];
  if (selectedSpendBy && spendBy === ISpendByType.SERVICE_TOWER) {
    items = groupedByContribution.filter(
      (it) => it.dimValueL2 === category && selectedSpendBy === it.dimValueL1
    );
  } else {
    items = groupedByContribution.filter((it) => it.dimValueL1 === category);
  }
  return items.reduce(
    (acc: number, item) =>
      acc +
      getItemDataValue({
        item,
        displayBy,
        revenue,
      }),
    0
  );
};
